import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { companyService } from "../services/company.service";

const errMsg = (err, fallback) =>
  err?.response?.data?.message || err?.response?.data?.detail || fallback;

export const fetchCompanies = createAsyncThunk(
  "company/fetchAll",
  async (_, thunkAPI) => {
    try {
      return await companyService.getCompanies();
    } catch (err) {
      return thunkAPI.rejectWithValue("Failed to fetch companies");
    }
  },
);

export const fetchActiveCompany = createAsyncThunk(
  "company/fetchActive",
  async (_, thunkAPI) => {
    try {
      return await companyService.getActiveCompany();
    } catch (err) {
      return thunkAPI.rejectWithValue("Failed to fetch active company");
    }
  },
);

export const fetchCompanyById = createAsyncThunk(
  "company/fetchById",
  async (id, thunkAPI) => {
    try {
      return await companyService.getCompanyById(id);
    } catch (err) {
      return thunkAPI.rejectWithValue("Failed to fetch company");
    }
  },
);

export const addCompany = createAsyncThunk(
  "company/add",
  async (company, thunkAPI) => {
    try {
      return await companyService.createCompany(company);
    } catch (err) {
      return thunkAPI.rejectWithValue(errMsg(err, "Failed to create company"));
    }
  },
);

export const editCompany = createAsyncThunk(
  "company/edit",
  async ({ id, company }, thunkAPI) => {
    try {
      return await companyService.updateCompany(id, company);
    } catch (err) {
      return thunkAPI.rejectWithValue(errMsg(err, "Failed to update company"));
    }
  },
);

export const removeCompany = createAsyncThunk(
  "company/remove",
  async (id, thunkAPI) => {
    try {
      await companyService.deleteCompany(id);
      return id; // Returns the ID so the reducer knows what to remove
    } catch (err) {
      return thunkAPI.rejectWithValue("Failed to delete company");
    }
  },
);

export const configureCompanyPoints = createAsyncThunk(
  "company/configurePoints",
  async ({ id, points }, thunkAPI) => {
    try {
      return await companyService.configureCompanyPoints(id, points);
    } catch (err) {
      return thunkAPI.rejectWithValue("Failed to configure company points");
    }
  },
);

export const activateCompany = createAsyncThunk(
  "company/activate",
  async (id, thunkAPI) => {
    try {
      return await companyService.activateCompany(id);
    } catch (err) {
      return thunkAPI.rejectWithValue(
        errMsg(err, "Failed to activate company"),
      );
    }
  },
);

export const deactivateCompany = createAsyncThunk(
  "company/deactivate",
  async (id, thunkAPI) => {
    try {
      return await companyService.deactivateCompany(id);
    } catch (err) {
      return thunkAPI.rejectWithValue(
        errMsg(err, "Failed to deactivate company"),
      );
    }
  },
);

export const validateCompany = createAsyncThunk(
  "company/validate",
  async (id, thunkAPI) => {
    try {
      return await companyService.validateCompany(id);
    } catch (err) {
      return thunkAPI.rejectWithValue(errMsg(err, "Failed to validate company"));
    }
  }
);

const companySlice = createSlice({
  name: "company",
  initialState: {
    companies: [],
    company: null,
    loading: false,
    error: null,
  },
  reducers: {
    updateCompanyPhoto: (state, { payload }) => {
      if (!state.company?.photos) {
        state.company.photos = [];
      }
      if (payload.isPrincipal) {
        state.company.photos = state.company.photos.map((photo) => ({
          ...photo,
          isPrincipal: false,
        }));
      }
      state.company.photos = [payload, ...state.company.photos];
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchCompanies.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchCompanies.fulfilled, (state, action) => {
        state.loading = false;
        state.companies = action.payload;
      })
      .addCase(fetchCompanies.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(fetchCompanyById.fulfilled, (state, action) => {
        state.company = action.payload;
      })
      .addCase(addCompany.fulfilled, (state, action) => {
        state.companies.push(action.payload);
      })

      .addCase(editCompany.fulfilled, (state, action) => {
        const updated = action.payload;
        const targetId = updated.companyId || updated.id;
        const index = state.companies.findIndex(
          (c) => (c.companyId || c.id) === targetId,
        );
        if (index >= 0) state.companies[index] = updated;
      })

      .addCase(removeCompany.fulfilled, (state, action) => {
        const idToRemove = action.payload;
        state.companies = state.companies.filter(
          (c) => (c.companyId || c.id) !== idToRemove,
        );
      })

      .addCase(fetchActiveCompany.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchActiveCompany.fulfilled, (state, action) => {
        state.loading = false;
        state.company = action.payload;
      })
      .addCase(fetchActiveCompany.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(configureCompanyPoints.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(configureCompanyPoints.fulfilled, (state, action) => {
        state.loading = false;
        const targetId = action.meta.arg.id;

        const index = state.companies.findIndex(
          (c) => (c.companyId || c.id) === targetId,
        );
        if (index >= 0) {
          state.companies[index] = {
            ...state.companies[index],
            ...action.payload,
          };
        }

        if ((state.company?.companyId || state.company?.id) === targetId) {
          state.company = { ...state.company, ...action.payload };
        }
      })
      .addCase(configureCompanyPoints.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(activateCompany.fulfilled, (state, action) => {
        const c = state.companies.find(
          (x) => (x.companyId || x.id) === action.payload,
        );
        if (c) c.companyIsActive = true;
      })
      .addCase(deactivateCompany.fulfilled, (state, action) => {
        const c = state.companies.find(
          (x) => (x.companyId || x.id) === action.payload,
        );
        if (c) c.companyIsActive = false;
      })
      .addCase(validateCompany.fulfilled, (state, action) => {
        const targetId = action.payload?.companyId || action.meta.arg;
        const c = state.companies.find(
          (x) => (x.companyId || x.id) === targetId
        );
        if (c) c.companyIsValidated = true;

        if ((state.company?.companyId || state.company?.id) === targetId) {
          state.company = { ...state.company, companyIsValidated: true };
        }
      });
  },
});

export const { updateCompanyPhoto } = companySlice.actions;
export default companySlice.reducer;
